import { getAllPagesData, getOverviewGeneralSummary } from "../api/overview.api";

// Map backend response to UI-friendly structure
export async function fetchOverviewSummary(params) {
	// Prefer dedicated generalSummary endpoint when available, else fallback
	let raw;
	try {
		raw = await getOverviewGeneralSummary({ checkMfa: true });
	} catch {
		raw = await getAllPagesData(params);
	}
	// Normalize container: some APIs wrap data under 'data' or 'summary'
	const c = raw?.data ?? raw?.summary ?? raw ?? {};
	let metrics = [];
	let topPages = c?.top_pages || [];

	// Case 1: analytics-style payload (pages/visits/users/duration)
	if (
		c?.total_pages !== undefined ||
		c?.total_visits !== undefined ||
		c?.unique_users !== undefined ||
		c?.avg_session_duration !== undefined ||
		c?.avg_session_duration_ms !== undefined
	) {
		const totalPages = c?.total_pages ?? 0;
		const totalVisits = c?.total_visits ?? 0;
		const uniqueUsers = c?.unique_users ?? 0;
		// Support seconds or ms
		const avgSessionSeconds =
			c?.avg_session_duration !== undefined
				? Number(c.avg_session_duration)
				: (c?.avg_session_duration_ms !== undefined ? Number(c.avg_session_duration_ms) / 1000 : 0);
		metrics = [
			{ title: "Total Pages", value: num(totalPages), subtitle: "Pages in selected range", bgClass: "bg-[#E8F2FF]", badgeClass: "bg-white/70 border-blue-200", icon: "info" },
			{ title: "Total Visits", value: num(totalVisits), subtitle: "All visits", bgClass: "bg-[#F1EAFE]", badgeClass: "bg-white/70 border-purple-200", icon: "percent" },
			{ title: "Unique Users", value: num(uniqueUsers), subtitle: "Distinct visitors", bgClass: "bg-[#E8FFF7]", badgeClass: "bg-white/70 border-emerald-200", icon: "code" },
			{ title: "Avg Session Duration", value: formatDuration(avgSessionSeconds), subtitle: "hh:mm:ss", bgClass: "bg-[#E8FFF7]", badgeClass: "bg-white/70 border-teal-200", icon: "clock" }
		];
	}

	// Case 2: overview/accuracy-style payload provided by user
	if (
		c?.total_questions_asked !== undefined ||
		c?.accuracy !== undefined ||
		c?.total_product_count !== undefined
	) {
		const totalQuestions = c?.total_questions_asked ?? 0;
		const accuracy = c?.accuracy ?? 0;
		const totalProductCount = c?.total_product_count ?? 0;
		const answerConfidence = c?.answer_confidence_score;
		const fourth = answerConfidence !== undefined
			? { title: "Answer Confidence Score", value: pct(answerConfidence), subtitle: "Average model confidence", bgClass: "bg-[#E8FFF7]", badgeClass: "bg-white/70 border-teal-200", icon: "clock" }
			: { title: "Queries Without Results", value: pct(c?.queries_without_results_rate ?? 0), subtitle: "Rate without relevant results", bgClass: "bg-[#E8FFF7]", badgeClass: "bg-white/70 border-teal-200", icon: "clock" };
		metrics = [
			{ title: "Total Questions Asked", value: num(totalQuestions), subtitle: "Total number of questions asked by users on the platform", bgClass: "bg-[#E8F2FF]", badgeClass: "bg-white/70 border-blue-200", icon: "info" },
			{ title: "Accuracy", value: pct(accuracy), subtitle: "Accuracy rate of answers given by the system", bgClass: "bg-[#F1EAFE]", badgeClass: "bg-white/70 border-purple-200", icon: "percent" },
			{ title: "Total Product Count", value: num(totalProductCount), subtitle: "Total system/documentation module indexed on the platform", bgClass: "bg-[#E8FFF7]", badgeClass: "bg-white/70 border-emerald-200", icon: "code" },
			fourth
		];
	}

	// Case 3: explicit generalSummary object
	if (metrics.length === 0 && c?.generalSummary) {
		const g = c.generalSummary || {};
		const hours = g?.timeCostSaved?.hours ?? g?.time_cost_saved_hours ?? 0;
		const dollars = g?.timeCostSaved?.dollars ?? g?.time_cost_saved ?? g?.time_cost_saved_dollars ?? 0;
		const costText = `${num(hours)}h / $${num(dollars)}`;
		metrics = [
			{ title: "Total Questions Asked", value: num(g?.totalQuestions ?? g?.total_questions ?? 0), subtitle: "Total number of questions asked by users on the platform", bgClass: "bg-[#E8F2FF]", badgeClass: "bg-white/70 border-blue-200", icon: "info" },
			{ title: "Accuracy", value: pct(g?.accuracy ?? 0), subtitle: "Accuracy rate of answers given by the system", bgClass: "bg-[#F1EAFE]", badgeClass: "bg-white/70 border-purple-200", icon: "percent" },
			{ title: "Total Product Count", value: num(g?.totalProductCount ?? g?.total_product_count ?? 0), subtitle: "Total system/documentation module indexed on the platform", bgClass: "bg-[#E8FFF7]", badgeClass: "bg-white/70 border-emerald-200", icon: "code" },
			{ title: "Time/Cost Saved", value: costText, subtitle: "Estimated time and money savings from automation for the user", bgClass: "bg-[#E8FFF7]", badgeClass: "bg-white/70 border-teal-200", icon: "clock" }
		];
	}

	// Case 4: generic explicit metrics array
	if (metrics.length === 0 && Array.isArray(c?.metrics)) {
		metrics = c.metrics
			.filter(Boolean)
			.map((it) => {
				const title = it.title ?? it.name ?? it.label ?? "Metric";
				const rawVal = it.value ?? it.count ?? it.total ?? it.amount ?? it.score ?? it.v;
				return {
					title: String(title),
					value: formatValue(rawVal),
					subtitle: it.subtitle ?? it.description ?? "",
					bgClass: "bg-[#E8F2FF]",
					badgeClass: "bg-white/70 border-blue-200",
					icon: "info"
				};
			});
	}

	// Case 5: generic primitive fields on the root object (best‑effort)
	if (metrics.length === 0 && c && typeof c === "object") {
		const entries = Object.entries(c).filter(
			([, v]) => v !== null && (typeof v === "number" || typeof v === "string")
		);
		metrics = entries.slice(0, 8).map(([k, v]) => ({
			title: humanizeKey(k),
			value: formatValue(v),
			subtitle: "",
			bgClass: "bg-[#E8F2FF]",
			badgeClass: "bg-white/70 border-blue-200",
			icon: "info"
		}));
	}

	// Normalize top pages if backend uses a different key
	if (!Array.isArray(topPages)) {
		topPages = Array.isArray(c?.topPages) ? c.topPages : [];
	}

	return { metrics, topPages, raw };
}

function num(n) {
	try { return Number(n).toLocaleString(); } catch { return n; }
}
function pct(n) {
	if (n === undefined || n === null) return "-";
	try {
		let val = Number(n);
		if (val > 0 && val <= 1) {
			val = val * 100;
		}
		return `${Number.isFinite(val) ? val.toLocaleString() : n}%`;
	} catch {
		return `${n}%`;
	}
}
function formatDuration(seconds) {
	if (!seconds || seconds <= 0) return "00:00:00";
	const s = Math.floor(seconds % 60).toString().padStart(2, "0");
	const m = Math.floor((seconds / 60) % 60).toString().padStart(2, "0");
	const h = Math.floor(seconds / 3600).toString().padStart(2, "0");
	return `${h}:${m}:${s}`;
}
function humanizeKey(k) {
	try {
		return String(k)
			.replace(/[_-]+/g, " ")
			.replace(/([a-z0-9])([A-Z])/g, "$1 $2")
			.replace(/\s+/g, " ")
			.trim()
			.replace(/\b\w/g, (m) => m.toUpperCase());
	} catch {
		return String(k);
	}
}
function formatValue(v) {
	if (v === null || v === undefined) return "";
	if (typeof v === "number") return num(v);
	return String(v);
}


